import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Search, Trash2, Eye, EyeOff, Mail, Phone, Loader2, UserPlus,
  ChevronDown, ChevronRight, KeyRound, Building2, GraduationCap, Users as UsersIcon, Plus,
} from 'lucide-react';
import { getAuthToken } from '../lib/authToken';
import { formatNom, formatPrenom } from '../lib/personne';
import { CiviliteSelect } from '../components/CiviliteSelect';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const FONCTION_TO_ROLE = {
  formateur: 'formateur',
  commercial: 'commercial',
  referent_pedagogique: 'responsable_pedagogique',
  'referent pedagogique': 'responsable_pedagogique',
  administrateur: 'admin',
  administratif: 'administratif',
};
const FONCTION_LABELS = {
  formateur: 'Formateur', commercial: 'Commercial', referent_pedagogique: 'Réf. pédagogique',
  administratif: 'Administratif', administrateur: 'Administrateur', testeur: 'Testeur', responsable_session: 'Resp. de session',
};
const rolesFromFonctions = (fonctions) => [...new Set((fonctions || []).map(f => FONCTION_TO_ROLE[String(f).toLowerCase()]).filter(Boolean))];

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Administrateur',
  responsable_pedagogique: 'Resp. Pédagogique',
  administratif: 'Administratif',
  formateur: 'Formateur',
  commercial: 'Commercial',
  candidat: 'Candidat',
  client: 'Client',
};

const ROLE_COLORS = {
  super_admin: 'bg-amber-100 text-amber-700',
  admin: 'bg-red-100 text-red-700',
  responsable_pedagogique: 'bg-violet-100 text-violet-700',
  administratif: 'bg-sky-100 text-sky-700',
  formateur: 'bg-blue-100 text-blue-700',
  commercial: 'bg-emerald-100 text-emerald-700',
  candidat: 'bg-slate-100 text-slate-700',
  client: 'bg-cyan-100 text-cyan-700',
};

const COLLABORATEUR_ROLES = ['admin', 'responsable_pedagogique', 'administratif', 'formateur', 'commercial'];

const PasswordReset = ({ userId, label, headers, testId }) => {
  const [pwd, setPwd] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleReset = async () => {
    if (pwd.length < 6) { toast.error('6 caractères minimum'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/admin/users/${userId}/reset-password`, { password: pwd }, { headers });
      toast.success(`Mot de passe réinitialisé (${res.data.comptes_mis_a_jour} compte(s))`);
      setPwd('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <KeyRound className="w-4 h-4 text-slate-400 shrink-0" />
      <div className="relative flex-1 max-w-[240px]">
        <Input
          type={show ? 'text' : 'password'}
          placeholder={label || 'Nouveau mot de passe'}
          value={pwd}
          onChange={e => setPwd(e.target.value)}
          className="h-8 text-xs pr-8"
          data-testid={`${testId}-input`}
        />
        <button type="button" className="absolute right-2 top-2 text-slate-400" onClick={() => setShow(s => !s)}>
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleReset} disabled={saving || !pwd} data-testid={`${testId}-btn`}>
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Réinitialiser'}
      </Button>
    </div>
  );
};

const UsersPage = () => {
  const [activeTab, setActiveTab] = useState('collaborateurs');
  const [users, setUsers] = useState([]);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [clientsAcces, setClientsAcces] = useState({ clients: [], comptes_orphelins: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  // Collaborateur dialog
  const [collabDialogOpen, setCollabDialogOpen] = useState(false);
  const [editingCollab, setEditingCollab] = useState(null);
  const [fiches, setFiches] = useState([]);
  const [selectedFicheId, setSelectedFicheId] = useState('');
  const navigate = useNavigate();
  const [collabForm, setCollabForm] = useState({ email: '', civilite: '', nom: '', prenom: '', telephone: '', roles: [], password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [savingCollab, setSavingCollab] = useState(false);

  // Client access dialog
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [clientTarget, setClientTarget] = useState(null); // { client_id, client_nom, contacts }
  const [clientForm, setClientForm] = useState({ email: '', civilite: '', nom: '', prenom: '', telephone: '', password: '' });
  const [savingClient, setSavingClient] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'collab'|'user', ... }

  const token = getAuthToken();
  const headers = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [usersRes, collabRes, clientsRes, fichesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`, { headers }),
        axios.get(`${API_URL}/admin/collaborateurs`, { headers }),
        axios.get(`${API_URL}/admin/clients-acces`, { headers }).catch(() => ({ data: { clients: [], comptes_orphelins: [] } })),
        axios.get(`${API_URL}/formateurs`, { headers }).catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data);
      setCollaborateurs(collabRes.data);
      setClientsAcces(clientsRes.data);
      setFiches(fichesRes.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ========= Collaborateurs =========
  const openNewCollab = () => {
    setEditingCollab(null);
    setSelectedFicheId('');
    setCollabForm({ email: '', civilite: '', nom: '', prenom: '', telephone: '', roles: [], password: '' });
    setCollabDialogOpen(true);
  };

  // Fiches collaborateurs (menu Collaborateurs) sans compte d'accès
  const emailsAvecCompte = new Set(collaborateurs.map(c => (c.email || '').toLowerCase()));
  const fichesSansCompte = fiches.filter(f => f.email && !emailsAvecCompte.has(f.email.toLowerCase()));

  const selectFiche = (ficheId) => {
    setSelectedFicheId(ficheId);
    const f = fiches.find(x => x.id === ficheId);
    if (!f) return;
    setCollabForm(prev => ({
      ...prev,
      email: f.email || '',
      civilite: f.civilite || '',
      nom: f.nom || '',
      prenom: f.prenom || '',
      telephone: f.telephone || f.telephone_portable || '',
      roles: rolesFromFonctions(f.fonctions),
    }));
  };

  const openEditCollab = (collab) => {
    setEditingCollab(collab);
    setCollabForm({
      email: collab.email,
      civilite: collab.civilite || '',
      nom: collab.nom || '',
      prenom: collab.prenom || '',
      telephone: collab.telephone || '',
      roles: collab.roles.filter(r => COLLABORATEUR_ROLES.includes(r)),
      password: '',
    });
    setCollabDialogOpen(true);
  };

  const toggleRole = (role) => {
    setCollabForm(prev => ({
      ...prev,
      roles: prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role],
    }));
  };

  const handleSaveCollab = async () => {
    if (!editingCollab && !selectedFicheId) { toast.error('Sélectionnez un collaborateur'); return; }
    if (!collabForm.email) { toast.error('Email requis'); return; }
    if (collabForm.roles.length === 0) { toast.error(editingCollab ? 'Sélectionnez au moins un rôle' : 'Aucun rôle d\'accès ne découle des fonctions de ce collaborateur : complétez sa fiche dans le menu Collaborateurs'); return; }
    if (!editingCollab && !collabForm.password) { toast.error('Mot de passe requis pour un nouveau collaborateur'); return; }
    setSavingCollab(true);
    try {
      const payload = {
        email: collabForm.email,
        civilite: collabForm.civilite || null,
        nom: collabForm.nom,
        prenom: collabForm.prenom,
        telephone: collabForm.telephone,
        roles: collabForm.roles,
      };
      if (collabForm.password) payload.password = collabForm.password;
      if (editingCollab && editingCollab.email !== collabForm.email) payload.old_email = editingCollab.email;
      await axios.post(`${API_URL}/admin/collaborateurs`, payload, { headers });
      toast.success(editingCollab ? 'Collaborateur mis à jour' : 'Collaborateur créé');
      setCollabDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSavingCollab(false);
    }
  };

  // ========= Candidats / users génériques =========
  const toggleActive = async (u) => {
    try {
      await axios.put(`${API_URL}/admin/users/${u.id}`, { is_active: !u.is_active }, { headers });
      toast.success(u.is_active ? 'Compte désactivé' : 'Compte activé');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'collab') {
        for (const acc of deleteTarget.accounts) {
          await axios.delete(`${API_URL}/admin/users/${acc.id}`, { headers });
        }
        toast.success('Collaborateur supprimé');
      } else {
        await axios.delete(`${API_URL}/admin/users/${deleteTarget.id}`, { headers });
        toast.success('Compte supprimé');
      }
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  // ========= Clients =========
  const openClientAcces = (client, contact = null) => {
    setClientTarget(client);
    setClientForm({
      email: contact?.email || '',
      civilite: contact?.civilite || '',
      nom: contact?.nom || '',
      prenom: contact?.prenom || '',
      telephone: contact?.telephone || '',
      password: '',
    });
    setClientDialogOpen(true);
  };

  const handleSaveClientAcces = async () => {
    if (!clientForm.email) { toast.error('Email requis'); return; }
    if (!clientForm.password) { toast.error('Mot de passe requis'); return; }
    setSavingClient(true);
    try {
      await axios.post(`${API_URL}/admin/clients-acces`, { client_id: clientTarget.client_id, ...clientForm }, { headers });
      toast.success('Accès client créé');
      setClientDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSavingClient(false);
    }
  };

  const q = search.toLowerCase();
  const filteredCollabs = collaborateurs.filter(c =>
    !q || c.email?.toLowerCase().includes(q) || c.nom?.toLowerCase().includes(q) || c.prenom?.toLowerCase().includes(q));
  const candidatUsers = users.filter(u => u.role === 'candidat').filter(u =>
    !q || u.email?.toLowerCase().includes(q) || u.nom?.toLowerCase().includes(q) || u.prenom?.toLowerCase().includes(q));
  const filteredClients = clientsAcces.clients.filter(c =>
    !q || c.client_nom?.toLowerCase().includes(q) || (c.comptes || []).some(a => a.email?.toLowerCase().includes(q)));

  if (loading) {
    return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 space-y-4" data-testid="users-page">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Utilisateurs de l'application</h1>
          <p className="text-sm text-slate-500 mt-0.5">Accès à l'application : collaborateurs, candidats et clients</p>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setExpanded(null); }}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <TabsList>
              <TabsTrigger value="collaborateurs" data-testid="tab-collaborateurs">
                <UsersIcon className="w-4 h-4 mr-1.5" /> Collaborateurs ({collaborateurs.length})
              </TabsTrigger>
              <TabsTrigger value="candidats" data-testid="tab-candidats">
                <GraduationCap className="w-4 h-4 mr-1.5" /> Candidats ({users.filter(u => u.role === 'candidat').length})
              </TabsTrigger>
              <TabsTrigger value="clients" data-testid="tab-clients">
                <Building2 className="w-4 h-4 mr-1.5" /> Clients ({clientsAcces.clients.reduce((s, c) => s + (c.comptes?.length || 0), 0)})
              </TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-9 w-[200px]" data-testid="search-users" />
              </div>
              {activeTab === 'collaborateurs' && (
                <Button onClick={openNewCollab} className="bg-indigo-600 hover:bg-indigo-700 gap-2 h-9" data-testid="btn-new-collab">
                  <UserPlus className="w-4 h-4" /> Nouveau collaborateur
                </Button>
              )}
            </div>
          </div>

          {/* ============ COLLABORATEURS ============ */}
          <TabsContent value="collaborateurs" className="mt-4 space-y-2">
            {filteredCollabs.length === 0 && (
              <Card className="border-0 shadow-sm"><CardContent className="p-12 text-center text-slate-400">Aucun collaborateur</CardContent></Card>
            )}
            {filteredCollabs.map(collab => {
              const isOpen = expanded === `c-${collab.email}`;
              return (
                <Card key={collab.email} className="border-0 shadow-sm" data-testid={`collab-${collab.email}`}>
                  <CardContent className="p-0">
                    <button
                      type="button"
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                      onClick={() => setExpanded(isOpen ? null : `c-${collab.email}`)}
                      data-testid={`collab-row-${collab.email}`}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                          <span className="text-sm font-semibold text-indigo-700">
                            {(collab.prenom?.[0] || '').toUpperCase()}{(collab.nom?.[0] || '').toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{collab.nom} {collab.prenom}</p>
                          <p className="text-xs text-slate-400 flex items-center gap-1"><Mail className="w-3 h-3" />{collab.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="flex gap-1 flex-wrap justify-end">
                          {collab.roles.map(r => (
                            <Badge key={r} className={`${ROLE_COLORS[r] || 'bg-slate-100'} text-[10px] px-1.5 py-0`}>{ROLE_LABELS[r] || r}</Badge>
                          ))}
                        </div>
                        {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t px-4 py-4 space-y-4 bg-slate-50/50" data-testid={`collab-detail-${collab.email}`}>
                        <div className="flex items-center gap-4 flex-wrap text-xs text-slate-500">
                          {collab.telephone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{collab.telephone}</span>}
                          <span>{collab.accounts?.length || 0} compte(s) de rôle — mot de passe unique</span>
                        </div>
                        <PasswordReset
                          userId={collab.accounts?.[0]?.id}
                          label="Nouveau mot de passe (tous les rôles)"
                          headers={headers}
                          testId={`reset-collab-${collab.email}`}
                        />
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openEditCollab(collab)} data-testid={`edit-collab-${collab.email}`}>
                            Modifier (identité / rôles)
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-xs text-red-500 border-red-200 hover:bg-red-50"
                            onClick={() => setDeleteTarget({ type: 'collab', label: `${collab.nom} ${collab.prenom} (${collab.email})`, accounts: collab.accounts || [] })}
                            data-testid={`delete-collab-${collab.email}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* ============ CANDIDATS ============ */}
          <TabsContent value="candidats" className="mt-4 space-y-2">
            {candidatUsers.length === 0 && (
              <Card className="border-0 shadow-sm"><CardContent className="p-12 text-center text-slate-400">Aucun compte candidat</CardContent></Card>
            )}
            {candidatUsers.map(u => {
              const isOpen = expanded === `u-${u.id}`;
              return (
                <Card key={u.id} className="border-0 shadow-sm" data-testid={`candidat-user-${u.id}`}>
                  <CardContent className="p-0">
                    <button
                      type="button"
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                      onClick={() => setExpanded(isOpen ? null : `u-${u.id}`)}
                      data-testid={`candidat-user-row-${u.id}`}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                          <GraduationCap className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{u.nom} {u.prenom}</p>
                          <p className="text-xs text-slate-400 flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {!u.is_active && <Badge className="bg-red-100 text-red-700 text-[10px]">Désactivé</Badge>}
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Portail candidat</Badge>
                        {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t px-4 py-4 space-y-4 bg-slate-50/50">
                        <PasswordReset userId={u.id} headers={headers} testId={`reset-candidat-${u.id}`} />
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`}>
                            {u.is_active ? 'Désactiver le compte' : 'Activer le compte'}
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-xs text-red-500 border-red-200 hover:bg-red-50"
                            onClick={() => setDeleteTarget({ type: 'user', id: u.id, label: `${u.nom} ${u.prenom} (${u.email})` })}
                            data-testid={`delete-user-${u.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* ============ CLIENTS ============ */}
          <TabsContent value="clients" className="mt-4 space-y-2">
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg px-4 py-2.5 text-xs text-cyan-800" data-testid="clients-info-banner">
              Le portail entreprise est en construction : les accès créés ici permettront aux contacts de vos clients de s'y connecter.
            </div>
            {filteredClients.length === 0 && (
              <Card className="border-0 shadow-sm"><CardContent className="p-12 text-center text-slate-400">Aucune entreprise cliente</CardContent></Card>
            )}
            {filteredClients.map(client => {
              const isOpen = expanded === `cl-${client.client_id}`;
              return (
                <Card key={client.client_id} className="border-0 shadow-sm" data-testid={`client-${client.client_id}`}>
                  <CardContent className="p-0">
                    <button
                      type="button"
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                      onClick={() => setExpanded(isOpen ? null : `cl-${client.client_id}`)}
                      data-testid={`client-row-${client.client_id}`}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center shrink-0">
                          <Building2 className="w-5 h-5 text-cyan-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{client.client_nom}</p>
                          <p className="text-xs text-slate-400">{(client.contacts || []).length} contact(s) — {(client.comptes || []).length} accès actif(s)</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {(client.comptes || []).length > 0 && <Badge className="bg-cyan-100 text-cyan-700 text-[10px]">{client.comptes.length} accès</Badge>}
                        {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t px-4 py-4 space-y-4 bg-slate-50/50">
                        {(client.comptes || []).length > 0 && (
                          <div className="space-y-2">
                            <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wide">Accès existants</p>
                            {client.comptes.map(acc => (
                              <div key={acc.id} className="flex items-center justify-between bg-white rounded-lg border px-3 py-2" data-testid={`client-compte-${acc.id}`}>
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-slate-700 truncate">{acc.nom} {acc.prenom} <span className="text-slate-400">— {acc.email}</span></p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <PasswordReset userId={acc.id} headers={headers} testId={`reset-client-${acc.id}`} />
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:text-red-600"
                                    onClick={() => setDeleteTarget({ type: 'user', id: acc.id, label: `${acc.email}` })}
                                    data-testid={`delete-client-compte-${acc.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {(client.contacts || []).filter(ct => ct.email && !(client.comptes || []).some(a => a.email === (ct.email || '').toLowerCase())).length > 0 && (
                          <div className="space-y-2">
                            <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wide">Contacts sans accès</p>
                            {client.contacts.filter(ct => ct.email && !(client.comptes || []).some(a => a.email === (ct.email || '').toLowerCase())).map((ct, i) => (
                              <div key={ct.id || i} className="flex items-center justify-between bg-white rounded-lg border px-3 py-2">
                                <p className="text-xs text-slate-600 truncate">{ct.nom} {ct.prenom} <span className="text-slate-400">— {ct.email}</span></p>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openClientAcces(client, ct)} data-testid={`create-acces-contact-${client.client_id}-${i}`}>
                                  <Plus className="w-3 h-3 mr-1" /> Créer l'accès
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openClientAcces(client)} data-testid={`create-acces-${client.client_id}`}>
                          <UserPlus className="w-3 h-3 mr-1" /> Créer un accès manuellement
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        </Tabs>

        {/* Dialog collaborateur */}
        <Dialog open={collabDialogOpen} onOpenChange={setCollabDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="collab-dialog">
            <DialogHeader>
              <div className="flex items-center justify-between gap-4 mr-6">
                <DialogTitle>{editingCollab ? 'Modifier le collaborateur' : 'Nouveau collaborateur'}</DialogTitle>
                <Button size="sm" onClick={handleSaveCollab} disabled={savingCollab} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-collab-btn-top">
                  {savingCollab ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
                </Button>
              </div>
            </DialogHeader>
            <div className="space-y-4">
              {!editingCollab && (
                <div className="space-y-3" data-testid="collab-fiche-picker">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Collaborateur *</Label>
                    <Select value={selectedFicheId} onValueChange={selectFiche}>
                      <SelectTrigger data-testid="collab-fiche-select">
                        <SelectValue placeholder={fichesSansCompte.length ? 'Choisir un collaborateur sans accès…' : 'Tous les collaborateurs ont déjà un accès'} />
                      </SelectTrigger>
                      <SelectContent>
                        {fichesSansCompte.map(f => (
                          <SelectItem key={f.id} value={f.id} data-testid={`collab-fiche-option-${f.id}`}>
                            {f.nom} {f.prenom} <span className="text-slate-400">— {(f.fonctions || []).map(fn => FONCTION_LABELS[fn] || fn).join(', ') || 'sans fonction'}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-slate-400">
                      Le collaborateur doit d'abord exister dans le menu Collaborateurs.{' '}
                      <button type="button" className="text-indigo-600 hover:underline" onClick={() => { setCollabDialogOpen(false); navigate('/formateurs'); }} data-testid="collab-goto-fiches">
                        Créer une fiche collaborateur
                      </button>
                    </p>
                  </div>
                  {selectedFicheId && (
                    <div className="rounded-lg border bg-slate-50 p-3 text-sm space-y-2" data-testid="collab-fiche-resume">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-semibold text-slate-800">{collabForm.civilite ? `${collabForm.civilite} ` : ''}{collabForm.nom} {collabForm.prenom}</span>
                        <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {collabForm.email}</span>
                      </div>
                      <div>
                        <p className="text-[11px] text-slate-500 mb-1">Rôles d'accès (déduits des fonctions de la fiche)</p>
                        <div className="flex flex-wrap gap-1.5">
                          {collabForm.roles.length === 0 ? (
                            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5" data-testid="collab-no-role">Aucun rôle d'accès : ajoutez une fonction (formateur, réf. pédagogique, commercial, administratif…) sur sa fiche</span>
                          ) : collabForm.roles.map(role => (
                            <span key={role} className={`px-2.5 py-1 rounded-full text-xs font-medium border ${ROLE_COLORS[role] || ''}`} data-testid={`collab-derived-role-${role}`}>{ROLE_LABELS[role]}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {editingCollab && (<>
              <div className="grid grid-cols-[100px_1fr_1fr] gap-3">
                <CiviliteSelect value={collabForm.civilite} onChange={(v) => setCollabForm(f => ({ ...f, civilite: v }))} testId="collab-civilite" />
                <div className="space-y-1.5">
                  <Label className="text-xs">Prénom</Label>
                  <Input value={collabForm.prenom} onChange={e => setCollabForm(f => ({ ...f, prenom: formatPrenom(e.target.value) }))} data-testid="collab-prenom" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Nom</Label>
                  <Input value={collabForm.nom} onChange={e => setCollabForm(f => ({ ...f, nom: formatNom(e.target.value) }))} data-testid="collab-nom" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Email *</Label>
                  <Input type="email" value={collabForm.email} onChange={e => setCollabForm(f => ({ ...f, email: e.target.value }))} data-testid="collab-email" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Téléphone</Label>
                  <Input value={collabForm.telephone} onChange={e => setCollabForm(f => ({ ...f, telephone: e.target.value }))} data-testid="collab-telephone" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Rôles d'accès *</Label>
                <div className="flex flex-wrap gap-2">
                  {COLLABORATEUR_ROLES.map(role => {
                    const selected = collabForm.roles.includes(role);
                    return (
                      <button
                        key={role}
                        type="button"
                        onClick={() => toggleRole(role)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${selected ? ROLE_COLORS[role] + ' ring-2 ring-offset-1 ring-current' : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100'}`}
                        data-testid={`role-toggle-${role}`}
                      >
                        {selected ? '✓ ' : ''}{ROLE_LABELS[role]}
                      </button>
                    );
                  })}
                </div>
              </div>
              </>)}
              {(editingCollab || selectedFicheId) && (
              <div className="space-y-1.5">
                <Label className="text-xs">{editingCollab ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}</Label>
                <div className="relative">
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    value={collabForm.password}
                    onChange={e => setCollabForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Un seul mot de passe pour tous ses rôles"
                    data-testid="collab-password"
                  />
                  <button type="button" className="absolute right-3 top-2.5 text-slate-400" onClick={() => setShowPwd(s => !s)}>
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[10px] text-slate-400">À la connexion, s'il a plusieurs rôles, le collaborateur choisit son portail de travail.</p>
              </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCollabDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSaveCollab} disabled={savingCollab} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-collab-btn">
                {savingCollab ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog accès client */}
        <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
          <DialogContent className="max-w-md" data-testid="client-acces-dialog">
            <DialogHeader>
              <DialogTitle>Créer un accès — {clientTarget?.client_nom}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-[100px_1fr_1fr] gap-3">
                <CiviliteSelect value={clientForm.civilite} onChange={(v) => setClientForm(f => ({ ...f, civilite: v }))} testId="client-acces-civilite" />
                <div className="space-y-1.5">
                  <Label className="text-xs">Prénom</Label>
                  <Input value={clientForm.prenom} onChange={e => setClientForm(f => ({ ...f, prenom: formatPrenom(e.target.value) }))} data-testid="client-acces-prenom" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Nom</Label>
                  <Input value={clientForm.nom} onChange={e => setClientForm(f => ({ ...f, nom: formatNom(e.target.value) }))} data-testid="client-acces-nom" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={clientForm.email} onChange={e => setClientForm(f => ({ ...f, email: e.target.value }))} data-testid="client-acces-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Téléphone</Label>
                <Input value={clientForm.telephone} onChange={e => setClientForm(f => ({ ...f, telephone: e.target.value }))} data-testid="client-acces-telephone" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Mot de passe *</Label>
                <Input type="password" value={clientForm.password} onChange={e => setClientForm(f => ({ ...f, password: e.target.value }))} data-testid="client-acces-password" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setClientDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSaveClientAcces} disabled={savingClient} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-client-acces-btn">
                {savingClient ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer l'accès"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmation suppression */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce compte ?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTarget?.type === 'collab'
                  ? `Tous les comptes de rôle de ${deleteTarget?.label} seront définitivement supprimés. La fiche collaborateur (menu Collaborateurs) n'est pas affectée.`
                  : `Le compte de ${deleteTarget?.label} sera définitivement supprimé.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-user">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default UsersPage;
